import React, { createContext, useContext, useMemo, useRef } from 'react';
import cx from 'classnames';
import { PlacesType } from 'react-tooltip';
import { IsTruncatedOptions, useIsTruncated } from '@/hooks/useIsTruncated';
import TooltipWrapper from '@/components/base/TooltipWrapper/TooltipWrapper';
import styles from './TooltipGroup.module.scss';

type TooltipGroupProps = {
  /** To define group boundaries */
  id: string;
  /** If explicitly set to "false", would render tooltips for each element, wrapped with Anchor*/
  shouldShowForTruncatedOnly?: boolean;
  rootClassName?: string;
  /** ClassName to customise current group's tooltip container appearance */
  tooltipClassName?: string;
  childrenWrapperClassName?: string;
  /** Delay before showing tooltip in ms */
  delayShow?: number;
  children: React.ReactNode;
};

export type TooltipProps = {
  isMultiLine?: boolean;
  value: string;
  children: React.ReactNode | ((state: { isTruncated: boolean }) => React.ReactNode);
  isTruncatedOptions?: IsTruncatedOptions;
  /** Specify position of tooltip as per react-tooltip documentation */
  place?: PlacesType;
  /** Specify unique className for tooltip, which would be applied exactly for this AnchorGroup, extending tooltipClassname from TooltipGroup */
  className?: string;
  // This is needed for icon tooltip. Because icon is not a text, there would be no tooltip.
  forceDisplayTooltip?: boolean;
};

type TooltipGroupContextValue = {
  id?: string;
  shouldShowForTruncatedOnly: boolean;
  hasProvider: boolean;
};

const DEFAULT_TOOLTIP_GROUP_CONTEXT: TooltipGroupContextValue = {
  id: undefined,
  shouldShowForTruncatedOnly: true,
  hasProvider: false,
};

const TooltipGroupContext = createContext<TooltipGroupContextValue>(DEFAULT_TOOLTIP_GROUP_CONTEXT);

export const useTooltipGroupContext = () => {
  return useContext(TooltipGroupContext);
};

export const TooltipGroup: React.FC<TooltipGroupProps> = ({
  id,
  shouldShowForTruncatedOnly = true,
  rootClassName,
  tooltipClassName,
  childrenWrapperClassName,
  delayShow = 900,
  children,
}) => {
  const contextValue = useMemo(
    () => ({
      id: `${id}__tooltip`,
      shouldShowForTruncatedOnly,
      hasProvider: true,
    }),
    [id, shouldShowForTruncatedOnly],
  );

  const customStyles = useMemo(
    () => ({
      zIndex: 'var(--tooltip-z-index)',
    }),
    [],
  );

  const combinedTooltipClassName = cx(styles.tooltipWrapper, tooltipClassName);

  return (
    <TooltipGroupContext.Provider value={contextValue}>
      <div className={cx(styles.root, 'global-class', rootClassName)}>
        <div className={styles.tooltipWrapper}>
          <TooltipWrapper
            id={contextValue.id}
            place={'top'}
            className={combinedTooltipClassName}
            delayHide={200}
            delayShow={delayShow}
            style={customStyles}
            offset={4}
            clickable
          />
        </div>
        <div className={cx(styles.childrenWrapper, 'global-class', childrenWrapperClassName)}>{children}</div>
      </div>
    </TooltipGroupContext.Provider>
  );
};

export const TooltipGroupAnchor: React.FC<TooltipProps> = ({
  value,
  children,
  isTruncatedOptions,
  place,
  className,
  forceDisplayTooltip,
  isMultiLine,
}) => {
  const ref = useRef(null);

  const isTruncated = useIsTruncated(ref, isTruncatedOptions);
  const tooltipGroupContext = useTooltipGroupContext();
  const { id, shouldShowForTruncatedOnly } = tooltipGroupContext;
  const isHidden = shouldShowForTruncatedOnly ? !isTruncated : false;
  const renderedChildren = typeof children === 'function' ? children({ isTruncated }) : children;

  return (
    <div
      ref={ref}
      className={styles.tooltipAnchorWrapper}
      data-tooltip-id={id}
      data-tooltip-html={value}
      data-tooltip-hidden={forceDisplayTooltip ? false : isHidden}
      data-is-multiline={isMultiLine}
      {...(place && { ['data-tooltip-place']: place })}
      {...(className && { ['data-tooltip-class-name']: className })}
    >
      {renderedChildren}
    </div>
  );
};
